import * as React from "react";
import { useState } from "react";
import { Box, Flex, Heading, Icon, Image, Link, Text } from "@chakra-ui/react";
import { FiArrowRight, FiArrowUpRight } from "react-icons/fi";
import UserIcon from "assets/images/user_icon.png";
import AltUserIcon from "assets/images/other_user_icon.png";
import siteConfig from "configs/site-config";
import Socials from "components/socials";
import { linkProps, riseIn } from "components/page";

const faceStyle = {
  position: "absolute" as const,
  top: 0,
  left: 0,
  backfaceVisibility: "hidden" as const,
  WebkitBackfaceVisibility: "hidden" as const,
};

// Click the portrait to flip it over.
const Portrait = () => {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <Box
      as="button"
      type="button"
      onClick={() => setIsFlipped(!isFlipped)}
      aria-label="Flip portrait"
      boxSize={{ base: "84px", md: "96px" }}
      borderRadius="full"
      boxShadow="0 0 0 5px var(--chakra-colors-bg), 0 0 0 6px var(--chakra-colors-line), 0 0 0 11px var(--chakra-colors-bg), 0 0 0 12px var(--chakra-colors-line)"
      sx={{ perspective: "600px" }}
      _focusVisible={{ outline: "2px solid", outlineColor: "accent", outlineOffset: "16px" }}
    >
      <Box
        position="relative"
        boxSize="full"
        transition="transform 0.7s cubic-bezier(0.2, 0.7, 0.2, 1)"
        transform={isFlipped ? "rotateY(180deg)" : "none"}
        sx={{ transformStyle: "preserve-3d" }}
      >
        <Image
          src={UserIcon}
          alt="Portrait of Daniel Haşegan"
          boxSize="full"
          borderRadius="full"
          objectFit="cover"
          sx={faceStyle}
        />
        <Image
          src={AltUserIcon}
          alt="Illustrated portrait of Daniel Haşegan as an astronaut"
          boxSize="full"
          borderRadius="full"
          objectFit="cover"
          transform="rotateY(180deg)"
          sx={faceStyle}
        />
      </Box>
    </Box>
  );
};

interface IndexLinkProps {
  name: string;
  path: string;
  hint: string;
  external?: boolean;
}

const IndexLink = ({ name, path, hint, external }: IndexLinkProps) => {
  return (
    <Link
      {...linkProps(path, external)}
      data-group
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={4}
      py={{ base: 3.5, md: 4 }}
      borderBottom="1px solid"
      borderColor="line"
    >
      <Text
        as="span"
        fontSize={{ base: "lg", md: "xl" }}
        transition="color 0.15s ease-out, transform 0.25s ease-out"
        _groupHover={{ color: "accent", transform: "translateX(4px)" }}
      >
        {name}
      </Text>
      <Flex as="span" align="center" gap={3} color="muted">
        <Text as="span" fontFamily="mono" fontSize="xs">
          {hint}
        </Text>
        <Icon
          as={external ? FiArrowUpRight : FiArrowRight}
          transition="color 0.15s ease-out, transform 0.25s ease-out"
          _groupHover={{ color: "accent", transform: "translateX(3px)" }}
        />
      </Flex>
    </Link>
  );
};

const Home = () => {
  return (
    <Flex minH="100vh" align="center" px={6} py={{ base: 20, md: 24 }}>
      <Box w="full" maxW="560px" mx="auto">
        {/* Inset so the outer ring, not the photo, lines up with the text column. */}
        <Box pl={3} pt={3} sx={riseIn(0)}>
          <Portrait />
        </Box>

        <Heading
          as="h1"
          mt={{ base: 10, md: 12 }}
          fontSize={{ base: "5xl", md: "7xl" }}
          lineHeight={1}
          sx={riseIn(1)}
        >
          {siteConfig.author.name}
        </Heading>

        <Text mt={4} fontSize={{ base: "lg", md: "xl" }} color="muted" maxW="32ch" sx={riseIn(2)}>
          PhD student at NYU Neuroscience Institute studying human perception and traveling waves of neural activity.
        </Text>

        <Box as="nav" aria-label="Site" mt={{ base: 10, md: 14 }} borderTop="1px solid" borderColor="line" sx={riseIn(3)}>
          {siteConfig.pages.map((page) => (
            <IndexLink key={page.path} {...page} />
          ))}
        </Box>

        <Socials mt={6} ml={-2} sx={riseIn(4)} />
      </Box>
    </Flex>
  );
};

export default Home;
